/* eslint-disable react-hooks/exhaustive-deps */
import { Divider, Form } from 'antd'
import { CustomLink } from 'src/components'
import { useTranslation } from 'next-i18next'
import React, { useState, useEffect } from 'react'
import { showNotification } from 'src/core/helpers'
import { PurchaseBrokeragePayload } from 'src/core/models'
import { useAppDispatch, useAppSelector, useWindowSize } from 'src/app/hooks'
import {
  resetBrokerageOperationState, validateEmailForStockSaleSelector,
  validateProjectIdForStockSaleSelector, createBrokerageOperationSelector,
  registerBrokerageUserSelector, resetValidateEmailState, selectedBrokerageTab,
  createSaleBrokerageOperation,
} from 'src/features'
import { brokerageFields, resetBrokerageForm } from './purchase-brokerage-sections'
import BrokerageErrorModal from '../StockPurchaseTabContent/BrokerageErrorModal/BrokerageErrorModal'
import ButtonsContent from '../StockPurchaseTabContent/StockPurchaseForm/ButtonsContent/ButtonsContent'
import RegisterUserModal from '../StockPurchaseTabContent/StockPurchaseForm/RegisterUserModal/RegisterUserModal'

const StockSaleTabContent: React.FC = () => {
  const [form] = Form.useForm()
  // Hooks
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { isMobile } = useWindowSize()
  // States
  const [newLink, setNewLink] = useState(null)
  const [isUserRegisterModalVisible, setIsUserRegisterModalVisible] = useState<boolean>(false)
  const [onResetFile, setOnResetFile] = useState<boolean>(false)
  const [currentOffsetReason, setCurrentOffsetReason] = useState<number>(null)
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [purchaseCurrency, setPurchaseCurrency] = useState<string>('EUR')
  // Selectors
  const { email } = useAppSelector(registerBrokerageUserSelector)
  const selectedTab = useAppSelector(selectedBrokerageTab)

  /* Validate Email Selector */
  const { error: errorEmailValidation } = useAppSelector(validateEmailForStockSaleSelector)

  /* Validate Project Id Selector */
  const { response: validateProjectIdResponse } = useAppSelector(validateProjectIdForStockSaleSelector)

  /* Submit Operation Selector */
  const {
    loading: loadingOperation,
    success: successOperation,
    error: errorCreatePurchaseOperation,
    newPurchaseLink
  } = useAppSelector(createBrokerageOperationSelector)

  // Use Effect
  useEffect(() => (() => resetForm(false)), [])

  useEffect(() => {
    form.setFieldsValue({ certificateLanguage: 'en' })
  }, [])

  useEffect(() => {
    if (errorCreatePurchaseOperation && errorCreatePurchaseOperation?.type) {
      setIsModalVisible(true)
    }
  }, [errorCreatePurchaseOperation])

  useEffect(() => {
    if (successOperation) {
      resetForm(false)
      showNotification('success', t('admin.Brokerage.SuccessOperation'))
      setNewLink(newPurchaseLink)
      dispatch(resetBrokerageOperationState())
    }
  }, [successOperation])

  useEffect(() => {
    if (selectedTab === 'STOCK' && email) {
      form.setFieldsValue({ 'sellerEmail': email })
      dispatch(resetValidateEmailState())
    }
  }, [email])

  useEffect(() => {
    if (onResetFile) {
      setOnResetFile(false)
    }
  }, [onResetFile])


  const onFinish = (values: PurchaseBrokeragePayload) => {
    if (projectIdHasError || isUserEmailInvalid) {
      showNotification('error', t('admin.Brokerage.CheckProjecIdAndEmail'))
      return
    }
    setNewLink(null)
    dispatch(createSaleBrokerageOperation({ ...values, purchaseCurrency }))
  }

  const isUserEmailInvalid = ![null, 'SUCCESS'].includes(errorEmailValidation)
  const projectIdHasError = ['ERROR'].includes(validateProjectIdResponse)

  const resetForm = (showResetMessage = true) => {
    resetBrokerageForm(dispatch, setNewLink, form, showResetMessage, t, setCurrentOffsetReason, setOnResetFile)
  }

  return <>
    {isMobile && <Divider />}
    <Form
      name='basic'
      onFinish={onFinish}
      autoComplete='off'
      form={form}
    >
      {/* Seller User */}
      <brokerageFields.BrokerageSellerUserField
        isUserEmailInvalid={isUserEmailInvalid}
        setIsUserRegisterModalVisible={setIsUserRegisterModalVisible}
      />

      {/* Project Id */}
      <brokerageFields.BrokerageProjectIdField />

      {/* Stock Field */}
      <brokerageFields.BrokerageStockField />

      {/* Sale Price */}
      <brokerageFields.BrokerageSalePriceField
        purchaseCurrency={purchaseCurrency}
        setPurchaseCurrency={setPurchaseCurrency}
      />

      {/* Presented To Field */}
      <brokerageFields.BrokeragePresentedToField />

      {/* Created At Field */}
      <brokerageFields.BrokerageCreatedAtField />

      {/* Certificate Language Field */}
      <brokerageFields.BrokerageCertificateLanguage />

      {/* Offset Reasons */}
      <brokerageFields.BrokerageOffsetReasonField
        form={form}
        currentOffsetReason={currentOffsetReason}
        setCurrentOffsetReason={setCurrentOffsetReason}
      />
      <brokerageFields.BrokerageInvoiceNumberField />
      <brokerageFields.BrokerageInvoiceDocumentField onResetFile={onResetFile} />
      {/* Buttons Content */}
      <ButtonsContent handleResetForm={resetForm} loading={loadingOperation} />

      {/* New Purchase Url */}
      {newLink && <CustomLink link={newLink} label={t('admin.Brokerage.CheckLink')} />}
    </Form>

    <BrokerageErrorModal
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      error={errorCreatePurchaseOperation}
    />

    <RegisterUserModal
      isModalVisible={isUserRegisterModalVisible}
      setIsModalVisible={setIsUserRegisterModalVisible}
      isOnlyOffsetter={true}
    />
  </>
}

export default StockSaleTabContent
